import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, CircleNotch, Hourglass, SignOut } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function ForcedPasswordChange() {
  const { refresh } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Lösenordet måste vara minst 8 tecken.");
    if (pw !== confirm) return setError("Lösenorden matchar inte.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { new_password: pw });
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6" data-testid="forced-password-screen">
      <div className="w-full max-w-sm bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
        <div className="h-11 w-11 rounded-xl bg-[#0033FF] flex items-center justify-center mb-5">
          <ShieldCheck size={22} weight="bold" className="text-white" />
        </div>
        <h1 className="font-display font-bold text-2xl tracking-tight">Välj ett nytt lösenord</h1>
        <p className="mt-2 text-sm text-zinc-500">Du loggade in med ett temporärt lösenord. Välj ett eget för att fortsätta.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          {error && <div data-testid="forced-pw-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Nytt lösenord</label>
            <input data-testid="new-password-input" type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
              className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" placeholder="••••••••" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Bekräfta lösenord</label>
            <input data-testid="confirm-password-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" placeholder="••••••••" />
          </div>
          <button data-testid="save-new-password-button" type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-6 py-2.5 font-semibold transition-colors disabled:opacity-60">
            {loading && <CircleNotch size={18} className="animate-spin" />} Spara och fortsätt
          </button>
        </form>
      </div>
    </div>
  );
}

export function PendingApproval() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-6" data-testid="pending-approval-screen">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center justify-center mb-8">
          <img src="/senzum-logo-white.png" alt="Senzum AI" className="h-8 w-auto" />
        </div>
        <div className="h-16 w-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-6">
          <Hourglass size={30} weight="bold" className="text-[#5C7CFF]" />
        </div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Ditt konto väntar på godkännande</h1>
        <p className="mt-3 text-zinc-400 leading-relaxed">
          Tack för att du registrerade dig. En administratör behöver godkänna ditt konto och tilldela en roll innan du får tillgång till arbetsytan.
        </p>
        <button data-testid="pending-logout-button" onClick={() => { logout(); navigate("/login"); }}
          className="mt-8 inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 rounded-md px-5 py-2.5 font-semibold transition-colors">
          <SignOut size={18} weight="bold" /> Logga ut
        </button>
      </div>
    </div>
  );
}
